import React, { useId, useState, FC, ChangeEvent, useEffect } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Heading, Text, Types, CheckBox, Button } from '@marriott/mi-ui-library';
import { StyledExperienceDetails } from './ExperienceDetails.styles';
import { ExperienceDetailsProps, amenitiesDetailsProps } from './ExperienceDetails.types';
import { SurveyWrapper } from '../../molecules/SurveyWrapper';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import { TextArea } from '../../atoms/TextArea';
import { CarouselContainer } from '../../molecules/CarouselContainer';
import { brandColorSchemeGenerator, activitiesCheckboxData, getPassionData } from '../../util/constants';
import { usePathname, useRouter } from 'next/navigation';
import dayjs from 'dayjs';
import DOMPurify from 'isomorphic-dompurify';
import moment from 'moment';
import { updateDatalayer } from '../../util/dataLayer/datalayerUtil';
import { SurveyWrapperInner } from '../../molecules/SurveyWrapperInner';
import { removeXSSScripts } from '../../util/helpers';
import { validateStayNotStarted } from '../../util/prearrivalValidation';
import { ErrorDetail } from '../Error';
import { getResConfirmationDatePrefix } from '../../util/dataLayer/resConfirmationNumberUtil';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ExperienceDetails: FC<ExperienceDetailsProps> = (props: any) => {
  const experienceCategoryConst = 'activities_page';
  const { formData, setFormData, form_prearrival_questions_complete, setTotalPoint } = useCreateSurveyStore();

  const { isShowError: stayValidationError, errorMsg: stayErrorMsg } = validateStayNotStarted({
    arrivalDate: formData?.reservation?.arrival,
    isAuthorMode: props?.isAuthorMode ?? false,
    confirmationNumber: formData?.confirmationNumber || null,
    marshaCode: formData?.marshaCode || null,
  });

  const [marshaCode, setMarshaCode] = useState('');
  const [propertyName, setPropertyName] = useState('');
  const passions = formData?.passions ? getPassionData(formData?.passions) : {};
  const title = props.model?.headerText;
  const setDescription = props.model?.hotelDescription;
  const diningExpText = props.model?.otherActivitiesDescription;
  const hotelText = `${props.model?.hotelText}<b> ${propertyName}</b>`;
  const arrivalDate = dayjs(formData?.reservation?.arrival).format('dddd MM/DD/YYYY');
  const departureDate = dayjs(formData?.reservation?.departure).format('dddd MM/DD/YYYY');
  const [alternateCID, setAlternateCID] = useState('');
  const [amenitiesData, setAmenitiesData] = useState([]);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [currentDate, setCurrentDate] = useState(moment().format('MMDDYYYY'));

  const combinedValue = `${currentDate}${confirmationNumber}`;

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const i18nText: any = props?.i18nMap;

  const [experienceOptionsData, setExperienceOptionsData] = useState(
    Array<{ label: string; name: string; enabled: string }>
  );

  useEffect(() => {
    setTotalPoint();
    setMarshaCode(formData?.property_config?.marsha_cd);
    setPropertyName(formData?.property_config?.hotel_name);
    setAlternateCID(formData?.reservation?.AlternateCustomerId);
    setAmenitiesData(formData?.property_config?.amenities ?? props.property_config?.amenities);
    setConfirmationNumber(props?.reservation?.confirmationNumber || formData?.reservation?.confirmationNumber || '');
    setCurrentDate(getResConfirmationDatePrefix(props?.reservation?.confoCreate || formData?.reservation?.confoCreate));

    if (
      formData?.property_config?.other_experience_options?.length > 0 ||
      props?.property_config?.other_experience_options?.length > 0
    ) {
      setExperienceOptionsData(
        formData?.property_config?.other_experience_options ?? props?.property_config?.other_experience_options
      );
    } else {
      setExperienceOptionsData(activitiesCheckboxData);
    }

    if (!formData?.other_experiences_check) {
      if (formData?.other_experiences_details) {
        // init the array
        const experienceOptionsInitBasedOnPassions = [];

        // passion : get the passion data from acc
        if (passions) {
          if (formData?.property_config?.other_experience_options?.length > 0) {
            if (passions.beachWaterActivities === '1' || passions.swimming === '1') {
              experienceOptionsInitBasedOnPassions.push('Water activities');
            }
            if (
              passions.cardio === '1' ||
              passions.cycling === '1' ||
              passions.hikingAndWalking === '1' ||
              passions.mountainWinterSports === '1' ||
              passions.running === '1' ||
              passions.strengthTraining === '1' ||
              passions.tennis === '1' ||
              passions.yogaPilates === '1'
            ) {
              experienceOptionsInitBasedOnPassions.push('Wellness-related activities');
            }
            if (passions.golf === '1') {
              experienceOptionsInitBasedOnPassions.push('Golf activities');
            }
            if (passions.kidsActivities === '1') {
              experienceOptionsInitBasedOnPassions.push('Family-friendly activities');
            }
            if (
              passions.liveMusicConcerts === '1' ||
              passions.liveSports === '1' ||
              passions.localCultureExcursions === '1' ||
              passions.theatreFineArts === '1'
            ) {
              experienceOptionsInitBasedOnPassions.push('Explore the local area & culture');
            }
            if (passions.natureWildlife === '1') {
              experienceOptionsInitBasedOnPassions.push('Connect to the local environment');
            }
            if (passions.shopping === '1') {
              experienceOptionsInitBasedOnPassions.push('Shopping');
            }
            if (passions.spa === '1') {
              experienceOptionsInitBasedOnPassions.push('Spa activities');
            }
          } else {
            if (passions.beachWaterActivities === '1' || passions.swimming === '1') {
              experienceOptionsInitBasedOnPassions.push('Water activities');
            }
            if (
              passions.cardio === '1' ||
              passions.cycling === '1' ||
              passions.hikingAndWalking === '1' ||
              passions.mountainWinterSports === '1' ||
              passions.running === '1' ||
              passions.strengthTraining === '1' ||
              passions.tennis === '1' ||
              passions.yogaPilates === '1'
            ) {
              experienceOptionsInitBasedOnPassions.push('Wellness-related activities');
            }
            if (passions.golf === '1') {
              experienceOptionsInitBasedOnPassions.push('Golf activities');
            }
            if (passions.kidsActivities === '1') {
              experienceOptionsInitBasedOnPassions.push('Family-friendly activities');
            }
            if (
              passions.liveMusicConcerts === '1' ||
              passions.liveSports === '1' ||
              passions.localCultureExcursions === '1' ||
              passions.theatreFineArts === '1'
            ) {
              experienceOptionsInitBasedOnPassions.push('Explore the local area & culture');
            }
            if (passions.natureWildlife === '1') {
              experienceOptionsInitBasedOnPassions.push('Connect to the local environment');
            }
            if (passions.shopping === '1') {
              experienceOptionsInitBasedOnPassions.push('Shopping');
            }
            if (passions.spa === '1') {
              experienceOptionsInitBasedOnPassions.push('Spa activities');
            }
          }
        }
        let updatedArr1: string[] = [];

        const matchedExperienceOptions =
          experienceOptionsInitBasedOnPassions?.filter(option =>
            (formData?.property_config?.other_experience_options?.length ?? 0) > 0
              ? formData.property_config.other_experience_options.some(
                  (configOption: { label: string }) => configOption.label === option
                )
              : activitiesCheckboxData?.some((configOption: { label: string }) => configOption.label === option)
          ) ?? [];
        // Priority 1: use formData.other_experience_options if available
        if ((formData?.property_config?.other_experience_options?.length ?? 0) > 0) {
          const arr2Labels = formData.property_config.other_experience_options.map(
            (item: { label: string }) => item.label
          );
          updatedArr1 = matchedExperienceOptions.filter(item => arr2Labels.includes(item));

          // Priority 2: use props.other_experience_options if available
        } else {
          updatedArr1 = matchedExperienceOptions;
        }

        if (updatedArr1?.length > 0) {
          setFormData({
            ...formData,
            other_experiences_details: updatedArr1,
          });
        }
      }
    }
  }, [
    formData?.property_config?.marsha_cd,
    formData?.property_config?.hotel_name,
    formData?.reservation?.AlternateCustomerId,
    formData?.property_config?.amenities,
    formData?.reservation?.confirmationNumber,
    formData?.reservation?.confoCreate,
    formData?.property_config?.other_experience_options,
  ]);

  const pathName = usePathname();
  const router = useRouter();
  const handleNextPage = (newPart?: string) => {
    setTotalPoint();
    updateDatalayer('form_prearrival_questions_complete', form_prearrival_questions_complete);
    updateDatalayer('form_step_name', 'Review');
    const currentPath = pathName;
    const newPath = currentPath.replace(/\/[^/]*$/, `/${newPart}`);
    // handleRedirect(newPath);
    router.push(newPath);
  };
  /**
   * Determines whether compareDate satisfies the date range defined by startDate / endDate.
   *
   * Event mode rules (isEvent = true):
   *   • Both dates empty             → item is a Featured item; passes all event checks (returns true).
   *   • Only event_end_date present  → event already started; check arrival ≤ event_end only.
   *                                    Departure has no lower bound to enforce → true.
   *   • Only event_start_date present → event is open-ended (no end); check departure ≥ event_start only.
   *                                     Arrival has no upper bound to enforce → true.
   *   • Both dates present           → full overlap check:
   *                                     arrival ≤ event_end  AND  departure ≥ event_start.
   *
   * Non-event mode (isEvent = false):
   *   • Checks today falls within [valid_start, valid_end].
   *   • A missing bound is treated as open-ended on that side.
   */
  const isDateInRange = (
    startDate: string | undefined,
    endDate: string | undefined,
    compareDate: dayjs.Dayjs,
    isEvent: boolean = false,
    isArrival: boolean = false
  ): boolean => {
    const startEmpty = !startDate || startDate.trim() === '';
    const endEmpty = !endDate || endDate.trim() === '';

    // Both bounds missing — featured item or always-valid schedule; no date constraint to apply.
    if (startEmpty && endEmpty) return true;

    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (isEvent) {
      if (isArrival) {
        // Rule: arrival <= event_end
        // No event_end → event is open-ended or already started with no defined end; arrival is always valid.
        if (endEmpty || !end.isValid()) return true;
        return compareDate.isSame(end, 'day') || compareDate.isBefore(end, 'day');
      } else {
        // Rule: departure >= event_start
        // No event_start → event already started before any known date; departure is always valid.
        if (startEmpty || !start.isValid()) return true;
        return compareDate.isSame(start, 'day') || compareDate.isAfter(start, 'day');
      }
    } else {
      // Schedule validity: today must fall within [valid_start, valid_end].
      // A missing bound is open-ended on that side.
      const afterStart =
        startEmpty || !start.isValid() || compareDate.isSame(start, 'day') || compareDate.isAfter(start, 'day');
      const beforeEnd =
        endEmpty || !end.isValid() || compareDate.isSame(end, 'day') || compareDate.isBefore(end, 'day');
      return afterStart && beforeEnd;
    }
  };

  const arrivalDate1 = dayjs(formData?.reservation?.arrival);
  const departureDate2 = dayjs(formData?.reservation?.departure);

  const filteredAmenities = amenitiesData?.filter((item: amenitiesDetailsProps) => {
    const isValidCategory = item.category === experienceCategoryConst;
    const eventStartDate = item.event_start_date || '';
    const eventEndDate = item.event_end_date || '';
    const validStartDate = item.valid_start_date || '';
    const validEndEndDate = item.valid_end_date || '';

    // Checking if the current date falls within the valid schedule range
    const isValidSchedule = isDateInRange(validStartDate, validEndEndDate, dayjs());

    // Arrival must be on or before event_end. If event_end is empty, event is open-ended → always passes.
    const isValidArrivalDate = isDateInRange(eventStartDate, eventEndDate, arrivalDate1, true, true);

    // Departure must be on or after event_start. If event_start is empty, event already started → always passes.
    const isValidDepartureDate = isDateInRange(eventStartDate, eventEndDate, departureDate2, true, false);

    return isValidCategory && isValidArrivalDate && isValidDepartureDate && isValidSchedule;
  });

  const eventItems = filteredAmenities?.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => dayjs(data.event_start_date).isValid() || dayjs(data.event_end_date).isValid()
  );
  const featuredItems = filteredAmenities?.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any) => !dayjs(data.event_start_date).isValid() && !dayjs(data.event_end_date).isValid()
  );

  const labels = {
    mainHeading: title,
    subTitle1: hotelText,
    subTitle2: setDescription,
    diningExperinceTitle:
      filteredAmenities?.length > 0
        ? props.model?.otherActivitiesText
        : props.model?.otherActivitiesText.replace('Other ', ''),
    diningExpDetails: diningExpText,
  };

  const inputId = useId();

  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>(() =>
    formData?.other_experiences_details?.reduce((acc: { [key: string]: boolean }, item: string) => {
      acc[item] = true;
      return acc;
    }, {})
  );

  // Check if any checkbox is checked
  const [isAnyCheckboxChecked, setIsAnyCheckboxChecked] = useState(false);

  useEffect(() => {
    setIsAnyCheckboxChecked(
      Object.values(checkedItems).some(value => value) || formData?.other_experiences_details?.length > 0
    );
  }, [checkedItems, formData?.other_experiences_details]);

  // Handle checkbox change
  const handleActivityCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = e.target;
    setCheckedItems(prevState => ({
      ...prevState,
      [value]: checked,
    }));

    if (checked) {
      setFormData({
        ...formData,
        other_experiences_details: [...formData.other_experiences_details, removeXSSScripts(value)],
        other_experiences_check: true,
      });
    } else {
      if (formData.other_experiences_details?.length === 1) {
        setFormData({
          ...formData,
          other_experiences_details: formData.other_experiences_details.filter((str: string) => str !== value),
          dietary_restrictions_notes: '',
          other_experiences_check: true,
        });
      } else {
        setFormData({
          ...formData,
          other_experiences_details: formData.other_experiences_details.filter((str: string) => str !== value),
          other_experiences_check: true,
        });
      }
    }
  };

  const sanitizedNode1 = DOMPurify.sanitize(labels.subTitle1, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString1 = sanitizedNode1.innerHTML;

  const sanitizedNode2 = DOMPurify.sanitize(labels.subTitle2, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString2 = sanitizedNode2.innerHTML;

  const sanitizedNode3 = DOMPurify.sanitize(labels.diningExpDetails, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString3 = sanitizedNode3.innerHTML;

  const [colorSchemeforPage, setColorSchemeforPage] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [brandCodeProps, setBrandCodeProps] = useState('');
  useEffect(() => {
    if (props?.brandCode) {
      if (props?.property_config?.non_branded_flg === 'Y') {
        setColorSchemeforPage(brandColorSchemeGenerator('NBF'));
        setBrandCodeProps('NBF');
      } else if (props.brandName === 'Ritz-Carlton Reserve') {
        setColorSchemeforPage(brandColorSchemeGenerator('RZR'));
        setBrandCodeProps('RZR');
      } else {
        setColorSchemeforPage(brandColorSchemeGenerator(props?.brandCode));
        setBrandCodeProps(props?.brandCode);
      }
    } else {
      if (formData?.property_config?.non_branded_flg === 'Y') {
        setColorSchemeforPage(brandColorSchemeGenerator('NBF'));
        setBrandCodeProps('NBF');
      } else {
        setColorSchemeforPage(brandColorSchemeGenerator(formData?.property_config?.brand_cd || formData?.brandCode));
        setBrandCodeProps(formData?.property_config?.brand_cd || formData?.brandCode);
      }
    }

    if (props?.backgroundImage) {
      setBackgroundImageUrl(props?.backgroundImage);
    } else {
      setBackgroundImageUrl(formData?.property_config?.background_image_url);
    }
  }, [
    props?.backgroundImage,
    props?.brandCode,
    formData?.property_config?.brand_cd,
    formData?.property_config?.background_image_url,
  ]);

  return (
    <EditableComponent {...props}>
      {stayValidationError ? (
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={colorSchemeforPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Experience"
          formName="Prearrival Travel Form"
          env_api_tile_error_info=""
          prearrival_form_amenities_added_to_plan={formData.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          form_prearrival_questions_complete={form_prearrival_questions_complete}
          beforeSuveryWrapperOuter={props?.model?.beforeSuveryWrapperOuter}
          afterSuveryWrapperOuter={props?.model?.afterSuveryWrapperOuter}
        >
          <ErrorDetail
            hotelEmail={props?.hotelEmail}
            hotelPhoneNumber={props?.hotelPhoneNumber}
            confirmationNumber={formData?.reservation?.confirmationNumber}
            errorVal={stayErrorMsg}
          />
        </SurveyWrapper>
      ) : (
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={colorSchemeforPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Experience"
          formName="Prearrival Travel Form"
          prearrival_form_amenities_added_to_plan={formData.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          env_api_tile_error_info=""
          form_prearrival_questions_complete={form_prearrival_questions_complete}
          beforeSuveryWrapperOuter={props?.model?.beforeSuveryWrapperOuter}
          afterSuveryWrapperOuter={props?.model?.afterSuveryWrapperOuter}
        >
          <div className="aem-GridColumn aem-GridColumn--default--12">
            <StyledExperienceDetails data-testid="experience-details" data-component-name="o-account-experienceDetails">
              <SurveyWrapperInner
                beforeSuveryWrapperInner={props?.model?.beforeSuveryWrapperInner}
                afterSuveryWrapperInner={props?.model?.afterSuveryWrapperInner}
              >
                <div className="experience-section">
                  <div className="container">
                    <div className="row">
                      <div className="col-12 pr-0 px-0">
                        <div className="lr-no-padding container col-12 col-xl-10">
                          <Heading
                            variation={Types.headingType.title}
                            fontSize={Types.size.large}
                            element={Types.tags.h1}
                            titleText={labels.mainHeading}
                          />
                          <div>
                            <Text
                              element={Types.tags.div}
                              fontSize={Types.size.small}
                              dangerouslySetInnerHTML={{ __html: fragmentString1 }}
                              customClass="subtext mb-2"
                            ></Text>

                            <Text
                              element={Types.tags.div}
                              fontSize={Types.size.medium}
                              dangerouslySetInnerHTML={{ __html: fragmentString2 }}
                              data-testid="ExperienceDetails Description"
                              customClass="noteText"
                            ></Text>
                          </div>
                          <div>
                            {eventItems && eventItems?.length > 0 && (
                              <Heading
                                variation={Types.headingType.title}
                                fontSize={Types.size.large}
                                element={Types.tags.h3}
                                titleText="Upcoming Events"
                                customClass="small-h6 mb-0 heading-top-margin"
                              />
                            )}
                            {eventItems?.length > 0 && (
                              <CarouselContainer
                                amenitiesDetails={eventItems}
                                componentType="Experience"
                                arrivalDate={arrivalDate}
                                departureDate={departureDate}
                                eventId="upcomingEventCar"
                                componentEventId="Experience_upcomingEventCar"
                                brandCode={brandCodeProps}
                              />
                            )}
                            {featuredItems && featuredItems?.length > 0 && (
                              <Heading
                                variation={Types.headingType.title}
                                fontSize={Types.size.extraSmall}
                                element={Types.tags.h3}
                                titleText={props?.i18nMap?.featuredExperiences || 'Featured Experiences'}
                                customClass="small-h6 mb-0 heading-top-margin"
                              />
                            )}
                            {featuredItems?.length > 0 && (
                              <CarouselContainer
                                amenitiesDetails={featuredItems}
                                componentType="Experience"
                                arrivalDate={arrivalDate}
                                departureDate={departureDate}
                                eventId="Featured"
                                componentEventId="Experience_Featured"
                                brandCode={brandCodeProps}
                              />
                            )}

                            {featuredItems?.length === 0 && <div className="mt-5"></div>}
                            {featuredItems?.length > 0 && <hr />}

                            <Heading
                              variation={Types.headingType.title}
                              fontSize={Types.size.extraSmall}
                              element={Types.tags.h3}
                              titleText={labels.diningExperinceTitle}
                              customClass="small-h6 mb-3"
                            />
                            <Text
                              element={Types.tags.paragraph}
                              fontSize={Types.size.small}
                              dangerouslySetInnerHTML={{ __html: fragmentString3 }}
                              customClass="mb-3"
                            ></Text>

                            <div className=" col-12 col-xl-10 px-0 isActive">
                              <div className="d-flex justify-content-between align-items-center flex-row flex-row-desktop mt-4">
                                <div className="row dietryref">
                                  {experienceOptionsData.map((dietbox, index) => (
                                    <div key={`dietbox-${index}`} className="col-md-6">
                                      <CheckBox
                                        checkboxName={dietbox.name}
                                        checkboxLabel={dietbox.label}
                                        checked={
                                          formData &&
                                          formData.other_experiences_details &&
                                          formData.other_experiences_details.includes(dietbox.label)
                                        }
                                        onChange={handleActivityCheckboxChange}
                                        checkboxId={`dietbox-${index}`}
                                        data-testid={`dietbox-${dietbox.name}`}
                                        key={`dietbox-${index}`}
                                        value={dietbox.label}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {isAnyCheckboxChecked && (
                                <TextArea
                                  id={`${inputId}-otherActivitiesExperienceArea`}
                                  key="otherActivitiesExperienceArea"
                                  name="otherActivitiesExperienceArea"
                                  label="otherActivitiesExperienceArea"
                                  placeHolderText={
                                    props?.i18nMap?.pleaseShareMoreDetails || 'Please share more details'
                                  }
                                  getInputValue={(val: string) => {
                                    setFormData({ ...formData, dietary_restrictions_notes: removeXSSScripts(val) });
                                  }}
                                  inputValue={removeXSSScripts(formData?.dietary_restrictions_notes)}
                                  maxLength={255}
                                  messageError={
                                    props?.i18nMap?.limitInput255 || 'Please limit your input to 255 characters.'
                                  }
                                ></TextArea>
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-end justify-content-end flex-row d-flex-mobile mobile-space col-12 p-0">
                            <Button
                              className="m-button-m m-button-secondary custom-btn mx-0 "
                              type={Types.ButtonTypeVariation.Button}
                              buttonCopy="Previous"
                              callback={() => handleNextPage('dining.mi')}
                            ></Button>
                            <Button
                              className="m-button-m m-button-primary custom-btn mx-0 "
                              type={Types.ButtonTypeVariation.Button}
                              buttonCopy={props?.i18nMap?.continue || 'Continue'}
                              callback={() => handleNextPage('review.mi')}
                            ></Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SurveyWrapperInner>
            </StyledExperienceDetails>
          </div>
        </SurveyWrapper>
      )}
    </EditableComponent>
  );
};
